import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Criteria } from "@esWebAPI/es/model/Criteria";
import type { Attribute } from "@esWebAPI/es/model/Feature";
import type { IEventChannel } from "@esWebAPI/es/model/Models";
import type FormatterFuncs from "@esWebAPI/es/util/formatterFuncs";

const emptyDynamicAttributesPromise = $.Deferred().resolve([]).promise(); // es.util.deferred.resolved([])

export default abstract class AbstractFilterControl {
    private readonly type;

    protected readonly eventChannel: IEventChannel;

    readonly layerViewModel;
    protected attrName?: string;
    protected readonly attrType?: string;
    protected readonly title?: string;

    protected locked: boolean;

    constructor(layerViewModel: LayerViewModel, type: string, attributeOrTitle?: Attribute | string) {
        // @ts-ignore checking for static methods as TypeScript doesn't have a way to specify children must have static methods
        if (!this.constructor.supportCriteria || !this.constructor.supportAttribute || !this.constructor.fromCriteria) {
            console.error("Extending Filter with no static support for criteria or attribute", this.constructor);
        }

        this.type = type;

        this.eventChannel = es.util.createEventChannel({
            onLockChanged: 1,
            onUpdate: 1,
            onDestroy: 1,
            onRemove: 1
        }, "es.control.Filter");

        this.layerViewModel = layerViewModel;
        if (attributeOrTitle) {
            if (typeof attributeOrTitle == "string") {
                this.title = attributeOrTitle;
            } else {
                const attribute = attributeOrTitle;

                this.attrName = attribute.name;
                this.attrType = attribute.attrType;
                this.title = attribute.title;
            }
        }

        this.locked = false;
    }

    /**
     * Initialise the Filter, *must* be called after the constructor. Will retrieve required data from the server if any.
     *
     * @returns Promise<Filter>
     */
    init(formatterFuncs?: FormatterFuncs): JQueryDeferred<AbstractFilterControl> {
        return es.util.deferred.resolved(this);
    }

    getEventChannel(): IEventChannel {
        return this.eventChannel;
    }

    getType(): string {
        return this.type;
    }

    destroy(silent?: boolean) {
        if (this.eventChannel) {
            if (!silent) {
                this.eventChannel.fireEvent("onDestroy", this);
            }
            this.eventChannel.destroy();
            //this.eventChannel = null;
        }
    }

    remove() {
        this.eventChannel.fireEvent("onRemove", this);
        this.filterControl.removeFilter(this); // ??!!
    }

    canLock(): boolean {
        return true;
    }

    checkLock(): string | null {
        if (!this.isLocked() && !this.isValid()) { // check only if trying to lock
            return $i18n('_Cannot_Lock_Invalid_Filter');
        }
        return null;
    }

    setLock(lock: boolean) {
        this.locked = lock;
        this.eventChannel.fireEvent("onLockChanged", this);
    }

    lock() {
        this.setLock(true);
    }

    unlock() {
        this.setLock(false);
    }

    isLocked(): boolean {
        return this.locked;
    }

    isValid(): boolean {
        return true;
    }

    isNumeric(): boolean {
        return this.attrType === "INTEGER" || this.attrType === "DECIMAL";
    }

    checkRemove(control: AbstractFilterControl): string | null {
        return null;
    }

    canRemove(): boolean {
        return true;
    }

    getAttrName(other?: boolean): string | undefined {
        return this.attrName;
    }

    setRange(range) {
        // Do nothing
    }

    getLayerViewModel(): LayerViewModel {
        return this.layerViewModel;
    }

    getDynamicAttributes(_criteria?: Criteria): JQueryDeferred<Attribute[]> {
        return emptyDynamicAttributesPromise;
    }

    getCriteriaClone(): Criteria[] {
        return this.isValid() ? [this.toCriteria()] : [];
    }

    isSpatial(): boolean {
        return false;
    }

    getTitle(): string {
        return this.title || "";
    }

    affectOtherLayer() {
        return false;
    }

    match(filter: AbstractFilterControl) {
        return this !== filter // do not match itself !
            && this.type === filter.getType()
            && this.attrName === filter.getAttrName()
            && this.isLocked() === filter.isLocked();
    }

    merge(filter: AbstractFilterControl) {
        console.error("Unsupported merge for " + filter.getType() + " filter", filter);
        console.warn("Ignoring filter to merge");
    }

    getOtherLayerId(): number | undefined {
        throw new Error("Not implemented");
    }
 
    getOtherLayerCriteria(): Criteria | undefined {
        throw new Error("Not implemented");
    }

    abstract toCriteria(expand?: boolean): Criteria | null;

    abstract fromCriteria(criteria: Criteria | Criteria[], attributes: Attribute | Attribute[], range, layerModule, analysis, formatterFuncs: FormatterFuncs): JQueryDeferred<AbstractFilterControl> | AbstractFilterControl | void;
}
